
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/hooks/useUserAllowedRoles';

export interface DefaultUserSettings {
  id: string;
  default_roles: UserRole[];
  default_current_role: UserRole;
  updated_at: string;
  updated_by: string | null;
}

export class DefaultUserSettingsService {
  
  async getDefaultSettings(): Promise<DefaultUserSettings | null> {
    try {
      console.log('🔧 DEBUG: Načítám výchozí nastavení...');
      const { data, error } = await supabase
        .from('default_user_settings')
        .select('*')
        .single();

      if (error) {
        console.error('🔧 DEBUG: Chyba při načítání výchozích nastavení:', error);
        throw error;
      }
      
      console.log('🔧 DEBUG: Výchozí nastavení načteno:', data);
      return data;
    } catch (error) {
      console.error('Error fetching default settings:', error);
      throw error;
    }
  }

  async updateDefaultSettings(settings: {
    default_roles: UserRole[];
    default_current_role: UserRole;
  }): Promise<void> {
    try {
      // Kontrola, zda je současný uživatel admin
      await this.validateAdminPermission();

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Uživatel není přihlášen');

      const { error } = await supabase
        .from('default_user_settings')
        .update({
          default_roles: settings.default_roles,
          default_current_role: settings.default_current_role,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', (await this.getDefaultSettings())?.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating default settings:', error);
      throw error;
    }
  }

  /**
   * Applies the configured default allowed-roles + current role to the
   * currently signed-in user.
   *
   * Direct client writes to user_allowed_roles (self) and profiles.role are
   * blocked by RLS / guard triggers to prevent privilege escalation. The
   * apply_default_roles() SECURITY DEFINER RPC performs both for auth.uid().
   * `userId` is accepted for call-site compatibility but the RPC always targets
   * the authenticated user (which is the just-registered user at signup).
   */
  async applyDefaultRolesToUser(userId: string): Promise<void> {
    try {
      console.log('🔧 DEBUG: Aplikuji výchozí role pro uživatele (self):', userId);

      const { error } = await (supabase.rpc as CallableFunction)('apply_default_roles');

      if (error) {
        console.error('🔧 DEBUG: Chyba při aplikaci výchozích rolí:', error);
        throw error;
      }

      console.log('🔧 DEBUG: Aplikace výchozích rolí dokončena úspěšně');
    } catch (error) {
      console.error('Error applying default roles to user:', error);
      throw error;
    }
  }

  private async validateAdminPermission(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Uživatel není přihlášen');

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    if (profile?.role !== 'admin') {
      throw new Error('Nemáte oprávnění pro tuto operaci');
    }
  }
}

export const defaultUserSettingsService = new DefaultUserSettingsService();
